import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbHighlight } from '@ng-bootstrap/ng-bootstrap';

export interface IDropdown {
  value: string | boolean;
  text: string;
  filterField?: string;
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.css'],
  imports: [CommonModule, ReactiveFormsModule, NgbDropdownModule, NgbHighlight],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownComponent implements ControlValueAccessor {
  readonly placeHolder = input<string>();
  readonly filterable = input<boolean>();
  readonly data = input<IDropdown[]>();
  // Writable model: writeValue()/onSelect() set the current option. No consumer
  // used two-way binding (there was no dropdownOptionChange output), so existing
  // one-way `[dropdownOption]` bindings behave exactly as before.
  readonly dropdownOption = model<IDropdown>();
  readonly disabled = input<boolean>();
  readonly isInvalid = input<boolean>();
  readonly primaryKeyFilter = input<string>();
  readonly sortDataByText = input<boolean>();
  readonly filterChange = output<string>();
  readonly valueChange = output<string | boolean>();
  readonly isOpen = output<boolean>();
  private readonly filter = viewChild<ElementRef<HTMLInputElement>>('ddFilter');

  // Signal rather than a plain field (SEOMS-470). It is read by this template
  // and by two order-form templates through a template reference variable, so
  // it is genuine view state. Setting it from `(openChange)` already notified
  // change detection — that is a template event binding — but a signal makes
  // the guarantee intrinsic to the field instead of dependent on every future
  // caller happening to be a template handler.
  readonly ddIsOpen = signal(false);

  // Reactive replacement for the old ngDoCheck: filter by primaryKeyFilter and
  // optionally sort a COPY (never mutate the input array).
  readonly displayData = computed(() => {
    const raw = this.data() ?? [];
    const filterKey = this.primaryKeyFilter();
    const filtered = filterKey
      ? raw.filter(dataRow => dataRow.filterField === filterKey)
      : raw;
    return this.sortDataByText()
      ? [...filtered].sort((a, b) => (a.text > b.text ? 1 : -1))
      : filtered;
  });

  f = new UntypedFormGroup({
    filterInput: new UntypedFormControl(''),
  });

  public onTouched = () => {};
  public onChange = () => {};

  writeValue(wv: string | boolean): void {
    const ddLookup = (this.data() ?? []).filter(
      dropdown => dropdown.value === wv,
    );
    if (ddLookup.length > 0) {
      this.dropdownOption.set({
        value: ddLookup[0].value,
        text: ddLookup[0].text,
        filterField: ddLookup[0].filterField,
      });
    }
  }

  registerOnChange(fn: unknown): void {
    this.valueChange.subscribe(fn as (value: string | boolean) => void);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onBlur(): void {
    this.onTouched();
  }

  onSelect(item: IDropdown): void {
    this.dropdownOption.set(item);
    this.valueChange.emit(item.value);
    this.resetFilter();
  }

  onKeyUp(): void {
    this.filterChange.emit(this.f.get('filterInput').value);
  }

  onOpenChange(isOpen: boolean): void {
    this.ddIsOpen.set(isOpen);
    this.isOpen.emit(isOpen);
    if (!isOpen) {
      this.resetFilter();
    } else {
      // Waits for ng-bootstrap to render the menu before focusing the filter.
      // Zoneless-safe (SEOMS-470): this only calls `focus()` on an existing DOM
      // node. It mutates no view state, so it needs no change detection — the
      // timer being untracked does not matter.
      setTimeout(() => {
        const filter = this.filter();
        if (filter) filter.nativeElement.focus();
      }, 50);
    }
  }

  resetFilter(): void {
    this.f.get('filterInput').setValue('');
    this.filterChange.emit(this.f.get('filterInput').value);
  }
}
